/*
 * @Author: Teemo Ding
 * @Date: 2022-03-03 14:30:09
 * @LastEditTime: 2022-05-06 15:30:47
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCommunication/Template/OrderPaymentReminder/index.tsx
 */
import {
  Checkbox,
  NumberInput,
  RadioGroup,
  RadioItem,
} from '@/src/components/Antd';
import SectionWrapper from '@/src/components/Layouts/Section/SectionWrapper';
import { CommunicationCategory, PeriodUnit } from '@/src/types/common/template';
import { Form } from 'antd';
import React, { Fragment } from 'react';
import { useParams } from 'react-router';
import { EnumMode } from 'src/components/Antd/NumberInput/NumberInput';
import { useI18n } from 'src/hooks/useRouterChange';
import { templateCategoryList } from 'src/pages/Settings/Communication/CommunicationTemplateDetail/config';
import PickUpTime from 'src/pages/Settings/Logistics/PickupLocations/Components/PickUpTime';
import { i18nGet } from 'src/tools/tools';
import GeneralInformation from '../../Components/GeneralInformation';
import css from '../../css.module.less';
import { generalInformationAll } from '../config';

import { useSalesChannel } from '@/src/constrains/SalesChannelHOC/useSalesChannel';
import { calc2Minute } from '../tools';

const flashSaleGroup = [
  CommunicationCategory.CreateOrder,
  CommunicationCategory.CreateShoppingCart,
  CommunicationCategory.AccountInvitation,
  CommunicationCategory.AccountPasswordReset,
  CommunicationCategory.VerificationCode,
  CommunicationCategory.CustomerServiceRequest,
  CommunicationCategory.FlashSaleStart,
];

const OrderStatusUpdateGroup = [
  CommunicationCategory.OrderStatus,
  CommunicationCategory.RefundStatus,
  // CommunicationCategory.OrderInvoice,
  CommunicationCategory.CreateSubscriptionBuyOrder,
  CommunicationCategory.ReturnStatus,
  CommunicationCategory.VirtualProductNotification,
  CommunicationCategory.GiftCardIssued,
  CommunicationCategory.GiftCardBalanceChanged,
  CommunicationCategory.StoreCreditIssued,
  CommunicationCategory.LogisticsStatusUpdate,
  CommunicationCategory.AccountWelcome,
  CommunicationCategory.RestockItem,
];

const communicationI18KMapping = {
  [CommunicationCategory.OrderStatus]:
    'salesChannelCommunication.index.afterorderplaced',
  [CommunicationCategory.PaymentReminder]:
    'salesChannelCommunication.index.afterorderplaced',
  [CommunicationCategory.AbandonedShoppingCart]:
    'salesChannelCommunication.status.abandonedShoppingCart',
  [CommunicationCategory.RefundStatus]:
    'salesChannelCommunication.status.refundStatus',
  [CommunicationCategory.ReturnStatus]:
    'salesChannelCommunication.status.returnStatus',
  [CommunicationCategory.CreateSubscriptionBuyOrder]:
    'salesChannelCommunication.status.createSubscriptionBuyOrder',
  [CommunicationCategory.OrderInvoice]:
    'salesChannelCommunication.index.afterorderplaced',
  [CommunicationCategory.VirtualProductNotification]:
    'salesChannelCommunication.status.virtualProductNotification',
  [CommunicationCategory.GiftCardIssued]:
    'salesChannelCommunication.status.giftCardIssued',
  [CommunicationCategory.GiftCardBalanceChanged]:
    'salesChannelCommunication.status.giftCardBalanceChanged',
  [CommunicationCategory.StoreCreditIssued]:
    'salesChannelCommunication.status.storeCreditIssued',
  [CommunicationCategory.LogisticsStatusUpdate]:
    'salesChannelCommunication.status.logisticsStatusUpdate',
  [CommunicationCategory.AccountInvitation]:
    'salesChannelCommunication.status.accountInvitation',
  [CommunicationCategory.AccountWelcome]:
    'salesChannelCommunication.status.accountWelcome',
  [CommunicationCategory.RestockItem]:
    'salesChannelCommunication.status.restockItem',
  [CommunicationCategory.VoucherIssued]:
    'salesChannelCommunication.status.voucherIssued',
  [CommunicationCategory.VoucherExpiration]:
    'salesChannelCommunication.status.voucherExpiration',
};

const periodUnitI18KMapping = {
  [PeriodUnit.Day]: 'general.day(s)',
  [PeriodUnit.Hour]: 'general.hour(s)',
  [PeriodUnit.Minute]: 'general.minute(s)',
};

const getInputNumberMin = (category) => {
  const minNumCategories = {
    [CommunicationCategory.VoucherExpiration]: 1,
  };
  return minNumCategories[category] || 0;
};

const allTemplateCategoryList = templateCategoryList.flatMap(
  (item) => item.children,
);

const InputDelayed = (props) => {
  const params: any = useParams();
  const { category } = params;
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { value, onChange, periodUnit, ...others } = props;
  return (
    <div className={css.notificationItem}>
      <NumberInput
        formProps={{ className: css.notificationItemInput }}
        suffix={
          getI18n(periodUnitI18KMapping[periodUnit || PeriodUnit.Minute]) || '-'
        }
        mode={EnumMode.int}
        value={value}
        onChange={onChange}
        size='large'
        min={1}
        {...others}
      />
      <span className={css.activeEndTimeInput}>
        {getI18n(communicationI18KMapping[category]) || '-'}
      </span>
    </div>
  );
};

const OrderPaymentReminder = ({ timezoneGmt, periodUnit }) => {
  const { orderCancellationTime, orderCancellationTimeUnit } =
    useSalesChannel();
  const orderCancellationTimeMinuts = calc2Minute(
    orderCancellationTime,
    orderCancellationTimeUnit,
  );
  const handleOnCalUnitUpper = () => {
    if (periodUnit === PeriodUnit.Minute) {
      return orderCancellationTimeMinuts;
    }
    if (periodUnit === PeriodUnit.Hour) {
      return orderCancellationTimeMinuts / 60;
    }
    if (periodUnit === PeriodUnit.Day) {
      return orderCancellationTimeMinuts / 60 / 24;
    }
    return orderCancellationTimeMinuts;
  };

  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const params: any = useParams();
  const { category } = params;

  const templateCategory = (allTemplateCategoryList || []).filter(
    (o) => o.value === category,
  );

  const inputNumberMin = getInputNumberMin(category);
  return (
    <SectionWrapper
      titleClassName={css.titleContainer}
      containerClassName={css.containerClassName}
      title={templateCategory[0].label}>
      <GeneralInformation
        generalInformationText={getI18n(generalInformationAll[category])}
      />
      {!flashSaleGroup.includes(category) && (
        <Fragment>
          <div className={css.notification}>
            {OrderStatusUpdateGroup.includes(category) ? (
              <Fragment>
                <Form.Item
                  label={getI18n(
                    'salesChannelCommunication.index.timeToSendNotification',
                  )}
                  name='delayedInstant'
                  initialValue={true}
                  className={css.notificationRadio}
                  labelCol={{ span: 24 }}>
                  <RadioGroup>
                    <div className={css.radioItem}>
                      <RadioItem value={true}>
                        {getI18n('salesChannelCommunication.index.instant')}
                      </RadioItem>
                    </div>
                    <div className={css.radioItem}>
                      <RadioItem value={false}>
                        {getI18n('salesChannelCommunication.index.delayed')}
                      </RadioItem>
                    </div>
                  </RadioGroup>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.delayedInstant !== currentValues.delayedInstant
                  }>
                  {({ getFieldValue }) =>
                    getFieldValue('delayedInstant') === false && (
                      <Form.Item
                        name='delayed'
                        className={css.noMarginFormItem}
                        labelCol={{ span: 24 }}>
                        <InputDelayed
                          periodUnit={periodUnit}
                          min={inputNumberMin}
                        />
                      </Form.Item>
                    )
                  }
                </Form.Item>
              </Fragment>
            ) : (
              <Form.Item
                label={getI18n(
                  'salesChannelCommunication.index.timeToSendNotification',
                )}
                name='delayed'
                labelCol={{ span: 24 }}
                rules={[
                  {
                    async validator(_, value) {
                      if (
                        handleOnCalUnitUpper() &&
                        value > handleOnCalUnitUpper()
                      ) {
                        return Promise.reject(
                          getI18n(
                            'salesChannelCommunication.index.timeToSendNotificationErrorTip',
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}>
                <InputDelayed
                  max={44640}
                  periodUnit={periodUnit}
                  min={inputNumberMin}
                />
              </Form.Item>
            )}
          </div>

          <Form.Item name='workingHoursEnabled' valuePropName='checked'>
            <Checkbox>
              {getI18n(
                'salesChannelCommunication.index.sendduringworkinghours',
              )}{' '}
              (UTC {timezoneGmt})
            </Checkbox>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(p, c) =>
              p.workingHoursEnabled !== c.workingHoursEnabled
            }>
            {({ getFieldValue }) =>
              getFieldValue('workingHoursEnabled') && <PickUpTime />
            }
          </Form.Item>
        </Fragment>
      )}
    </SectionWrapper>
  );
};

export default OrderPaymentReminder;
